import { Play, ExternalLink } from "lucide-react";
import { Reveal, Rise } from "./Reveal";
import content from "../content";
import { SectionBg } from "./SectionBg";

// Extrae el ID de un enlace de YouTube (o acepta el ID directamente).
function youtubeId(input) {
  if (!input) return null;
  const m = input.match(/(?:youtu\.be\/|v=|embed\/|shorts\/|live\/)([\w-]{11})/);
  if (m) return m[1];
  if (/^[\w-]{11}$/.test(input.trim())) return input.trim();
  return null;
}

export default function ServicesSection() {
  const { services } = content;

  // Oculta toda la sección cuando services.hidden = true (ver content.js).
  if (!services || services.hidden) return null;

  return (
    <section id="servicios" className="relative w-full bg-ink py-28 md:py-36 overflow-hidden" data-testid="services-section">
      <SectionBg image="/images/hero-1.webp" tint="ink" />
      <div
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[80vw] h-[36vw] max-w-[900px] opacity-30"
        style={{ background: "radial-gradient(ellipse at top, rgba(33,112,232,0.3), transparent 70%)" }}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-7xl mx-auto px-6">
        <Reveal className="max-w-3xl mb-14">
          <Rise>
            <p className="eyebrow mb-5">{services.eyebrow}</p>
          </Rise>
          <Rise>
            <h2 className="display text-white text-[clamp(2.25rem,6.5vw,5rem)] mb-6">
              {services.title}
            </h2>
          </Rise>
          <Rise>
            <p className="font-poppins text-ceramic/80 text-base md:text-lg leading-relaxed">
              {services.subtitle}
            </p>
          </Rise>
        </Reveal>

        <Reveal
          className={`grid grid-cols-1 gap-5 md:gap-6 ${services.videos.length > 1 ? "sm:grid-cols-2 max-w-4xl" : "max-w-2xl"}`}
          amount={0.1}
        >
          {services.videos.map((v, i) => {
            const id = youtubeId(v.youtube);
            const thumb = v.thumbnail || (id ? `https://img.youtube.com/vi/${id}/maxresdefault.jpg` : "");
            const watchUrl = id ? v.youtube || `https://www.youtube.com/watch?v=${id}` : null;

            const inner = (
              <>
                <div className="relative aspect-video w-full overflow-hidden">
                  {thumb ? (
                    <img
                      src={thumb}
                      alt={v.title}
                      loading="lazy"
                      onError={(e) => {
                        if (id && !e.currentTarget.dataset.fb) {
                          e.currentTarget.dataset.fb = "1";
                          e.currentTarget.src = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
                        }
                      }}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-white/[0.04]" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/25 to-transparent" />
                  {id ? (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="flex items-center justify-center w-16 h-16 rounded-full bg-electric/90 text-white shadow-[0_0_28px_rgba(33,112,232,0.7)] transition-transform duration-300 group-hover:scale-110">
                        <Play size={26} className="ml-1" fill="currentColor" />
                      </span>
                    </span>
                  ) : (
                    <span className="absolute top-3 right-3 rounded-full bg-white/15 backdrop-blur-md border border-white/20 px-3 py-1 font-poppins text-[11px] uppercase tracking-widest text-white">
                      Pronto
                    </span>
                  )}
                  {id && (
                    <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur-md border border-white/20 px-3 py-1 font-poppins text-[11px] uppercase tracking-widest text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <ExternalLink size={12} /> YouTube
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <p className="font-poppins text-electric text-xs tracking-[0.2em] uppercase mb-1.5">
                    {v.date}
                  </p>
                  <h3 className="display text-white text-xl md:text-2xl leading-none">
                    {v.title}
                  </h3>
                </div>
              </>
            );

            return (
              <Rise key={v.title}>
                {id ? (
                  <a
                    href={watchUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Ver en YouTube: ${v.title}`}
                    data-testid={`service-video-${i}`}
                    className={`card-sweep group relative block w-full overflow-hidden rounded-3xl text-left cursor-pointer ${
                      v.tag
                        ? "border border-electric/70 shadow-[0_0_36px_-6px_rgba(33,112,232,0.6)]"
                        : "border border-white/10"
                    }`}
                  >
                    {v.tag && (
                      <span
                        className="absolute top-3 left-3 z-10 rounded-full bg-electric px-3 py-1 font-poppins text-[11px] font-semibold uppercase tracking-widest text-white shadow-[0_0_18px_rgba(33,112,232,0.7)]"
                        data-testid={`service-video-tag-${i}`}
                      >
                        {v.tag}
                      </span>
                    )}
                    {inner}
                  </a>
                ) : (
                  <div
                    aria-label={`${v.title} · disponible pronto`}
                    data-testid={`service-video-${i}`}
                    className="group relative block w-full overflow-hidden rounded-3xl border border-white/10 text-left"
                  >
                    {inner}
                  </div>
                )}
              </Rise>
            );
          })}
        </Reveal>
      </div>
    </section>
  );
}
